/**
 * Availability filtering for the picker: which catalog attributes an author may reference at a given
 * point in the campaign. The rule is "everything captured up to this step":
 *  - `static` / `sf` / `reference` attributes are always available (they don't depend on prior steps);
 *  - `dynamic` attributes (captured answers, gaps, deep-dive findings) are available only once the flow
 *    is past the step that produces them.
 *
 * This module filters the campaign-agnostic catalog by an attribute's own `availability.afterStepOrder`.
 * Concrete per-instance dynamic options (e.g. `answer.<stepKey>.<questionKey>` for a specific captured
 * answer) are EXPANDED and assigned their originating step order by the option layer
 * (`Admin/.../useAttributeOptions`), which has the live steps + captured metadata; a family def without
 * a concrete `afterStepOrder` is treated as always-listable here and gated per instance there. Kept
 * dependency-free (no campaign row types) to stay a pure, testable leaf.
 */

import { ATTRIBUTE_CATALOG, type AttributeDef } from './catalog';

/** Whether `def` is referenceable when authoring a step at `uptoStepOrder` (undefined ⇒ terminal/report). */
export function isAvailableUpTo(def: AttributeDef, uptoStepOrder?: number): boolean {
  if (def.source !== 'dynamic') return true;
  const after = def.availability?.afterStepOrder;
  if (after == null) return true;
  return (uptoStepOrder ?? Infinity) > after;
}

/** The catalog defs referenceable at `uptoStepOrder`, in catalog order. */
export function attributesAvailableUpTo(uptoStepOrder?: number): AttributeDef[] {
  return ATTRIBUTE_CATALOG.filter((def) => isAvailableUpTo(def, uptoStepOrder));
}
